/**
 * useOutboxRecords (WI-6a-1, shell data hook) — a read-only view over the
 * device-local capture store that refreshes-on-change.
 *
 * Wraps `getAllOutboxRecords()` (db.ts, §1.1 — the read API already exists;
 * this hook adds NO new persistence) and re-reads on the SAME wake sources
 * the dispatcher (§1.5) uses to observe queue movement: an initial load,
 * the `online` event, and `visibilitychange`→visible (app foregrounded —
 * the common field pattern: the queue drained in the background, the log
 * should reflect it when the truck comes back into signal). A crash-recovery
 * startup scan (`recoverOnStartup()`, state-machine.ts) can flip SENDING→
 * QUEUED durably; the same visibility/online wakes surface that too.
 *
 * This is a pure READ hook: it never writes, never dispatches — the daily
 * log's own edit/undo/delete affordances (DailyLog, §4.3) drive the store.
 *
 * It ALSO subscribes to the store's own change signal (outbox/changes.ts).
 * That was added 2026-07-27 after saving a capture left the Daily Log showing
 * zero rows: `refresh` had to be called by every mutating caller, and the
 * capture-commit path — the most important one — never did. Store-driven
 * refresh makes that class of miss structural rather than per-call-site.
 * `refresh` remains exported for callers that want an immediate re-pull.
 */

import { useCallback, useEffect, useRef, useState } from "preact/hooks";
import { getAllOutboxRecords } from "../outbox/db";
import { onOutboxChanged } from "../outbox/changes";
import type { OutboxRecord } from "../outbox/types";

export interface UseOutboxRecords {
  records: OutboxRecord[];
  /** Re-reads the store now. Callers wire this into the per-item review/edit
   * surface's mutation callbacks (LogEntryReview onEdit/onDeleted/onCorrected/
   * onRemoved) so a local edit is reflected without waiting for a wake event. */
  refresh: () => Promise<void>;
}

export function useOutboxRecords(ownerPrincipal?: string, includeUnbound = false): UseOutboxRecords {
  const [records, setRecords] = useState<OutboxRecord[]>([]);
  const refreshGeneration = useRef(0);

  const refresh = useCallback(async () => {
    const generation = ++refreshGeneration.current;
    const rows = await getAllOutboxRecords();
    // Delivery and ASK stamping emit two adjacent writes. An older read must
    // never finish last and overwrite the newer, ASK-bearing snapshot.
    if (generation !== refreshGeneration.current) return;
    setRecords(ownerPrincipal
      ? rows.filter((row) => row.owner_principal === ownerPrincipal
        || (includeUnbound && row.owner_principal?.startsWith("unbound:") === true))
      : rows);
  }, [includeUnbound, ownerPrincipal]);

  useEffect(() => {
    void refresh();

    // Re-read whenever the store itself is written to. This is the load-bearing
    // subscription: saving a capture used to leave the log showing zero rows
    // because nothing told this hook to re-read, and the commit path was the one
    // caller that never invoked `refresh()`. Subscribing to the store means a
    // future write path cannot silently reintroduce that.
    // Registered BEFORE the window guard — the store signal has no dependency on
    // a DOM global, and it is the one source we must never miss.
    const unsubscribe = onOutboxChanged(() => void refresh());

    if (typeof window === "undefined") return unsubscribe;

    const wake = (): void => void refresh();
    const onVisibility = (): void => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("online", wake);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      unsubscribe();
      window.removeEventListener("online", wake);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [refresh]);

  return { records, refresh };
}
